<template>
  <div>
    <div class="block" style="padding: 0px 0px 7px 0px; border-bottom: solid 1px lightgray">
      <span class="has-text-weight-bold">Role Properties</span>
    </div>
    <spin-loader v-if="isLoading" />
    <div v-else>
      <div class="field">
        <label class="label is-size-7">
          Institution
          <span class="has-text-warning" style="padding-left: 3px">*</span>
        </label>
        <div class="control inline">
          <div class="select">
            <spin-loader v-if="isLoading" />
            <select v-else :class="{ input: true, 'is-danger': errors.institutionId !== undefined }" @change="handleInstitutionUpdate" v-model="input.institutionId">
              <option v-for="institution in institutionsArr" :value="institution.id" :key="institution.id">{{ institution.id }} {{ institution.name }}</option>
            </select>
          </div>
        </div>
      </div>
      <div class="level">
        <div class="level-left" style="width: 45%">
          <div class="field" style="width: 100%">
            <label class="label is-size-7">Report Type List</label>
            <spin-loader v-if="isLoading" />
            <div v-else class="control">
              <div class="select is-multiple" style="width: 100%">
                <select id="lstAvailableReportTypes" multiple v-model="selectedAvailableReportTypeCodes" :size="listSize" style="width: 100%">
                  <option v-for="code in sortedAvailableReportTypes" :value="code.code" :key="code.code">{{ code.name }}</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div class="level-center" style="width: 10%">
          <div id="buttonblock" class="has-text-centered">
            <div class="block">
              <button type="button" @click="moveReportTypeRight" class="button has-bcc-background has-text-white is-outlined">
                <i class="fas fa-caret-right fa-2x"></i>
              </button>
            </div>
            <div class="block">
              <button type="button" @click="moveReportTypeLeft" class="button has-bcc-background has-text-white is-outlined">
                <i class="fas fa-caret-left fa-2x"></i>
              </button>
            </div>
          </div>
        </div>

        <div class="level-right" style="width: 45%">
          <div class="field" style="width: 100%">
            <label class="label is-size-7">
              Permission to Access Report Types
              <span class="has-text-warning" style="padding-left: 3px">*</span>
            </label>
            <spin-loader v-if="isLoading" />
            <div v-else class="control" style="z-index: 0 !important">
              <div :class="{ 'select is-multiple': true, 'is-danger': errors.reportTypeCodes !== undefined }" style="width: 100%">
                <select id="lstPermittedReportTypes" multiple v-model="selectedPermittedReportTypeCodes" :size="listSize" style="width: 100%">
                  <option v-if="input.permittedReportTypeCodes.length === 0" disabled>Select a Report Type</option>
                  <option v-for="code in sortedPermittedReportTypes" :key="code.code" :value="code.code">{{ code.name }}</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useGlobalUtils } from '../helpers/globalUtils'
import axios from '../axios'

const { handleApiErr } = useGlobalUtils()

const props = defineProps({
  errors: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  model: {
    type: Object,
    default: () => ({
      id: '',
      firstName: '',
      middleName: '',
      lastName: '',
      email: '',
      phone: '',
      title: '',
      bio: '',
      extension: '',
      institutionIds: [],
      reportTypeCodes: [],
    }),
  },
})

const emit = defineEmits(['membershipUpdated'])

// Reactive data
const input = reactive({
  institutionId: 0,
  permittedReportTypeCodes: [],
})
const listSize = ref(7)
const institutions = ref(new Map())
const institutionsArr = ref([])
const selectedPermittedReportTypeCodes = ref([])
const selectedAvailableReportTypeCodes = ref([])
const reportTypes = ref(new Map())
const isLoading = ref(false)

// Computed properties
const reportTypeCodes = computed(() => [...reportTypes.value.keys()])

const availableReportTypeCodes = computed(() => {
  return reportTypeCodes.value.filter((x) => !input.permittedReportTypeCodes.includes(x))
})

const sortedAvailableReportTypes = computed(() => {
  return sortReportTypes(availableReportTypeCodes.value)
})

const sortedPermittedReportTypes = computed(() => {
  return sortReportTypes(input.permittedReportTypeCodes)
})

// Methods
function sortReportTypes(codes) {
  const objects = codes
    .filter((code) => reportTypes.value.has(code))
    .map((code) => reportTypes.value.get(code))
    .sort((a, b) => a.name.localeCompare(b.name))

  return objects
}

async function getReportTypesLookup() {
  try {
    isLoading.value = true
    const response = await axios.get('/report-types')
    reportTypes.value = new Map(response.data.records.map((i) => [i.code, i]))
  } catch (error) {
    handleApiErr(error)
  } finally {
    isLoading.value = false
  }
}

async function getInstitutionsLookup() {
  try {
    isLoading.value = true
    const response = await axios.get('/institutions')
    institutions.value = new Map(response.data.records.map((i) => [i.id, i]))
    institutionsArr.value = response.data.records
  } catch (error) {
    handleApiErr(error)
  } finally {
    isLoading.value = false
  }
}

function buildMembership() {
  return {
    userId: props.model.id,
    institutionIds: [input.institutionId],
    reportTypeCodes: input.permittedReportTypeCodes,
  }
}

function handleInstitutionUpdate() {
  emit('membershipUpdated', buildMembership())
}

function moveReportTypeRight() {
  input.permittedReportTypeCodes.push(...selectedAvailableReportTypeCodes.value)
  selectedAvailableReportTypeCodes.value = []

  emit('membershipUpdated', buildMembership())
}

function moveReportTypeLeft() {
  input.permittedReportTypeCodes = input.permittedReportTypeCodes.filter((x) => !selectedPermittedReportTypeCodes.value.includes(x))
  selectedPermittedReportTypeCodes.value = []

  emit('membershipUpdated', buildMembership())
}

// Lifecycle
onMounted(async () => {
  await getReportTypesLookup()
  await getInstitutionsLookup()

  if (props.model.institutionIds.length > 0) {
    input.institutionId = props.model.institutionIds[0]
  }
  input.permittedReportTypeCodes = [...props.model.reportTypeCodes]
})
</script>
